import React, { useState } from 'react';
import { submitProof, submitClaim } from '../api/ledger';

export default function Proofs() {
  const [mode, setMode] = useState<'proof' | 'claim'>('proof');
  const [agentId, setAgentId] = useState('');
  const [claimType, setClaimType] = useState('audit');
  const [statement, setStatement] = useState('');
  const [artifacts, setArtifacts] = useState('');
  const [executionTrace, setExecutionTrace] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmitProof = async () => {
    if (!agentId || !statement) return;
    setLoading(true);
    setResult(null);
    try {
      await submitProof({
        agentId,
        claimType,
        statement,
        artifacts: artifacts.split(',').map(a => a.trim()).filter(Boolean),
        executionTrace,
      });
      setResult({ type: 'success', text: 'Proof submitted successfully!' });
      setAgentId('');
      setStatement('');
      setArtifacts('');
      setExecutionTrace('');
    } catch (e) {
      setResult({ type: 'error', text: String(e) || 'Submission failed' });
    }
    setLoading(false);
  };

  const handleSubmitClaim = async () => {
    if (!agentId || !statement) return;
    setLoading(true);
    setResult(null);
    try {
      await submitClaim({ agentId, type: claimType, statement });
      setResult({ type: 'success', text: 'Claim registered successfully!' });
      setAgentId('');
      setStatement('');
    } catch (e) {
      setResult({ type: 'error', text: String(e) || 'Registration failed' });
    }
    setLoading(false);
  };

  return (
    <div className="ledger-container">
      <div className="ledger-header">
        <h1>📝 Proof & Claim Submission</h1>
        <p>Agent 작업 결과를 검증 가능한 Proof로 기록하거나, Claim을 등록합니다.</p>
      </div>

      <div className="ledger-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '24px' }}>
        <button
          className={`ledger-card ${mode === 'proof' ? '' : ''}`}
          onClick={() => setMode('proof')}
          style={{
            cursor: 'pointer',
            textAlign: 'left',
            border: mode === 'proof' ? '2px solid var(--ledger-accent)' : undefined,
          }}
        >
          <div className="ledger-card-title"><span className="ledger-icon">✅</span> Submit Proof</div>
          <p style={{ color: 'var(--ledger-muted)', fontSize: '13px', marginTop: '8px' }}>
            작업 완료 증거를 제출하고 검증을 요청합니다.
          </p>
        </button>
        <button
          className={`ledger-card`}
          onClick={() => setMode('claim')}
          style={{
            cursor: 'pointer',
            textAlign: 'left',
            border: mode === 'claim' ? '2px solid var(--ledger-accent)' : undefined,
          }}
        >
          <div className="ledger-card-title"><span className="ledger-icon">📋</span> Register Claim</div>
          <p style={{ color: 'var(--ledger-muted)', fontSize: '13px', marginTop: '8px' }}>
            새로운 Claim을 등록하여 검증 프로세스를 시작합니다.
          </p>
        </button>
      </div>

      <div className="ledger-card">
        <div className="ledger-card-header">
          <span className="ledger-card-title">
            <span className="ledger-icon">{mode === 'proof' ? '✅' : '📋'}</span>
            {mode === 'proof' ? 'Proof Submission' : 'Claim Registration'}
          </span>
        </div>

        {result && (
          <div style={{
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            background: result.type === 'success' ? 'rgba(63, 185, 80, 0.15)' : 'rgba(255, 71, 87, 0.15)',
            color: result.type === 'success' ? 'var(--ledger-success)' : '#ff4757',
            border: `1px solid ${result.type === 'success' ? 'rgba(63, 185, 80, 0.3)' : 'rgba(255, 71, 87, 0.3)'}`,
            fontSize: '14px',
          }}>
            {result.text}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
          <div>
            <label style={{ display: 'block', color: 'var(--ledger-muted)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Agent ID
            </label>
            <input
              className="ledger-input"
              placeholder="did:agent:001"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              disabled={loading}
            />
          </div>

          <div>
            <label style={{ display: 'block', color: 'var(--ledger-muted)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Claim Type
            </label>
            <select
              className="ledger-input"
              value={claimType}
              onChange={(e) => setClaimType(e.target.value)}
              disabled={loading}
            >
              <option value="audit">Audit</option>
              <option value="prediction">Prediction</option>
              <option value="research">Research</option>
              <option value="computation">Computation</option>
              <option value="verification">Verification</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', color: 'var(--ledger-muted)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Statement
            </label>
            <textarea
              className="ledger-input"
              placeholder="Describe the claim or work performed..."
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              rows={3}
              style={{ resize: 'vertical', minHeight: '80px' }}
              disabled={loading}
            />
          </div>

          {mode === 'proof' && (
            <>
              <div>
                <label style={{ display: 'block', color: 'var(--ledger-muted)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Artifacts (comma-separated)
                </label>
                <input
                  className="ledger-input"
                  placeholder="audit.pdf, result.csv, report.json"
                  value={artifacts}
                  onChange={(e) => setArtifacts(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: 'var(--ledger-muted)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Execution Trace
                </label>
                <input
                  className="ledger-input"
                  placeholder="0xtrace..."
                  value={executionTrace}
                  onChange={(e) => setExecutionTrace(e.target.value)}
                  disabled={loading}
                />
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button
              className="ledger-button"
              onClick={mode === 'proof' ? handleSubmitProof : handleSubmitClaim}
              disabled={loading || !agentId || !statement}
              style={{ opacity: loading || !agentId || !statement ? 0.5 : 1 }}
            >
              {loading ? '⏳ Submitting...' : mode === 'proof' ? '✅ Submit Proof' : '📋 Register Claim'}
            </button>
          </div>
        </div>
      </div>

      <div className="ledger-card" style={{ marginTop: '24px' }}>
        <div className="ledger-card-header">
          <span className="ledger-card-title"><span className="ledger-icon">ℹ️</span> Verification Process</span>
        </div>
        <div className="ledger-timeline">
          <div className="ledger-timeline-item">
            <strong style={{ color: 'var(--ledger-accent)' }}>1. Claim Registration</strong>
            <p style={{ color: 'var(--ledger-muted)', fontSize: '13px', marginTop: '4px' }}>
              Agent가 수행한 작업에 대한 Claim을 등록합니다.
            </p>
          </div>
          <div className="ledger-timeline-item">
            <strong style={{ color: 'var(--ledger-accent)' }}>2. Evidence Submission</strong>
            <p style={{ color: 'var(--ledger-muted)', fontSize: '13px', marginTop: '4px' }}>
              작업 결과물(Artifacts)과 실행 추적(Execution Trace)을 제출합니다.
            </p>
          </div>
          <div className="ledger-timeline-item">
            <strong style={{ color: 'var(--ledger-accent)' }}>3. Third-party Verification</strong>
            <p style={{ color: 'var(--ledger-muted)', fontSize: '13px', marginTop: '4px' }}>
              독립적인 검증자들이 2/3 합의를 통해 Proof를 검증합니다.
            </p>
          </div>
          <div className="ledger-timeline-item">
            <strong style={{ color: 'var(--ledger-accent)' }}>4. Reputation Update</strong>
            <p style={{ color: 'var(--ledger-muted)', fontSize: '13px', marginTop: '4px' }}>
              검증 결과에 따라 Agent의 Reputation 점수가 업데이트됩니다.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
